<template>
  <div class="d-flex h-100 position-relative" style="overflow: hidden;">
    <!-- Sidebar -->
    <v-card
      v-show="!mobile || drawerOpen"
      :class="mobile ? 'sidebar sidebar-mobile' : 'sidebar'"
      :elevation="mobile ? 4 : 0"
      rounded="0"
    >
      <v-list density="compact" nav>
        <v-progress-circular v-if="loading" class="ma-4 d-flex mx-auto" color="primary" indeterminate />
        <template v-else>
          <v-list-item
            v-for="b in availableButtons"
            :key="b.id"
            :active="selectedButton === b.id"
            :title="b.name"
            @click="selectButton(b.id)"
          />
          <v-list-item v-if="availableButtons.length === 0" disabled title="Aucun bouton" />
        </template>
        <v-divider class="my-1" />
        <v-list-item
          prepend-icon="mdi-plus"
          title="Ajouter un bouton"
          @click="selectButton('add-button')"
        />
      </v-list>
    </v-card>

    <!-- Overlay mobile -->
    <div
      v-if="mobile && drawerOpen"
      class="overlay-backdrop"
      @click="drawerOpen = false"
    />

    <!-- Contenu principal -->
    <v-sheet class="flex-fill h-100 overflow-auto">
      <v-btn
        v-if="mobile && selectedButton"
        class="ma-2"
        icon="mdi-arrow-left"
        size="small"
        variant="text"
        @click="drawerOpen = true"
      />
      <template v-if="selectedButton">
        <the-device-component :key="selectedButton" :device-id="selectedButton" @deleted="loadButtons" @update:device="onDeviceUpdated" />
      </template>
    </v-sheet>

    <!-- Dialog ajout de bouton -->
    <v-dialog v-model="showWizard" max-width="500" persistent>
      <the-bluetooth-wizard @close="closeWizard" @success="onWizardSuccess" />
    </v-dialog>
  </div>
</template>

<script lang="ts" setup>
  import type { Device } from '@/model/device.ts'
  import { storeToRefs } from 'pinia'
  import { onMounted, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useDisplay } from 'vuetify'
  import { deviceRepository } from '@/api/repository/device.repository.ts'
  import TheBluetoothWizard from '@/components/TheBluetoothWizard.vue'
  import TheDeviceComponent from '@/components/TheDeviceComponent.vue'
  import { useAppStore } from '@/stores/app.ts'
  import { notifyApiError } from '@/utils/error.utils.ts'

  const route = useRoute()
  const router = useRouter()
  const { mobile } = useDisplay()
  const { devicesDrawerOpen: drawerOpen } = storeToRefs(useAppStore())

  const selectedButton = ref<string | null>(null)
  const availableButtons = ref<Device[]>([])
  const loading = ref(true)
  const showWizard = ref(false)

  async function selectButton (id: string) {
    if (id === 'add-button') {
      showWizard.value = true
      return
    }
    if (mobile.value) {
      drawerOpen.value = false
    }
    await router.replace({ name: 'device', params: { deviceId: id }, query: {} })
    selectedButton.value = id
  }

  function closeWizard () {
    showWizard.value = false
  }

  function onWizardSuccess () {
    showWizard.value = false
    loadButtons()
    // Poll toutes les 10s pendant 1min pour détecter le nouveau bouton qui peut mettre un peu de temps à s'appairer
    let remaining = 6
    const poll = setInterval(() => {
      remaining--
      loadButtons()
      if (remaining <= 0) {
        clearInterval(poll)
      }
    }, 10_000)
  }

  function onDeviceUpdated (updated: Device) {
    const index = availableButtons.value.findIndex(b => b.id === updated.id)
    if (index !== -1) {
      availableButtons.value[index] = updated
    }
  }

  function syncSelectionFromRoute () {
    switch (route.name) {
      case 'device': {
        selectedButton.value = route.params.deviceId as string
        if (mobile.value) drawerOpen.value = false
        break
      }
      default: {
        selectedButton.value = null
        drawerOpen.value = true
        break
      }
    }
  }

  watch(() => route.fullPath, syncSelectionFromRoute)

  async function loadButtons () {
    try {
      availableButtons.value = await deviceRepository.list()
      syncSelectionFromRoute()
    } catch (error) {
      notifyApiError('Impossible de charger la liste des boutons', error)
    } finally {
      loading.value = false
    }
  }

  onMounted(() => {
    loading.value = true
    loadButtons()
  })
</script>

<style scoped>
.sidebar {
  width: 250px;
  min-width: 250px;
  height: 100%;
  overflow-y: auto;
  border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.sidebar-mobile {
  position: fixed;
  top: var(--v-layout-top, 64px);
  left: 0;
  height: calc(100vh - var(--v-layout-top, 64px));
  width: 80%;
  max-width: 300px;
  z-index: 11;
  overflow-y: auto;
}

.overlay-backdrop {
  position: fixed;
  top: var(--v-layout-top, 64px);
  left: 0;
  width: 100vw;
  height: calc(100vh - var(--v-layout-top, 64px));
  background: rgba(0, 0, 0, 0.5);
  z-index: 10;
}
</style>
